import React, { useEffect, useMemo, useState } from "react";
import {
  fetchSessionLogsForStudent,
  toDateMaybe,
} from "../../../../tutor/session_logs/sessionLogApi";
import {
  fetchReportsForStudent,
  renameReport,
  deleteReport,
} from "../../../../session_logs/progressReportApi";
import {
  buildMetrics,
  buildReportTitle,
  formatDateDisplay,
  formatDateRange,
  formatTime,
  sortByDateDesc,
} from "../../../../session_logs/progressReportLogic";
import { calcAvgRating, StarRating } from "../../../../session_logs/sessionRating";
import "./StudentSessionsTab.css";

const REPORTS_PER_PAGE = 5;
const SESSIONS_PER_PAGE = 10;

function hwBadgeTone(status) {
  const s = String(status || "").toLowerCase();
  if (s === "completed") return "ok";
  if (s.includes("partial")) return "warn";
  if (s === "not done") return "bad";
  return "neutral";
}

function riskTone(level) {
  const s = String(level || "").toLowerCase();
  if (s === "at risk") return "bad";
  if (s === "needs attention") return "warn";
  return "ok";
}

function Pager({ page, pageCount, onPage }) {
  if (pageCount <= 1) return null;
  return (
    <div className="sst-pager">
      <button type="button" className="sst-pager__btn" disabled={page <= 1} onClick={() => onPage(page - 1)}>
        ‹ Prev
      </button>
      <span className="sst-pager__info">
        Page {page} of {pageCount}
      </span>
      <button type="button" className="sst-pager__btn" disabled={page >= pageCount} onClick={() => onPage(page + 1)}>
        Next ›
      </button>
    </div>
  );
}

// Compact kebab menu for per-report actions (open / rename / delete).
// Tutors (canManage=false) get a plain open affordance — no rename/delete.
function ReportActions({ onOpen, onRename, onDelete, canManage = true }) {
  const [open, setOpen] = useState(false);
  if (!canManage) {
    return (
      <div className="sst-actions" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="sst-open-link" onClick={onOpen}>
          Open ↗
        </button>
      </div>
    );
  }
  return (
    <div className="sst-actions" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className="sst-kebab"
        aria-label="Report actions"
        onClick={() => setOpen((v) => !v)}
      >
        ⋮
      </button>
      {open && (
        <>
          <div className="sst-menu-backdrop" onClick={() => setOpen(false)} />
          <div className="sst-menu" role="menu">
            <button type="button" className="sst-menu-item" onClick={() => { setOpen(false); onOpen(); }}>
              Open report ↗
            </button>
            <button type="button" className="sst-menu-item" onClick={() => { setOpen(false); onRename(); }}>
              Rename
            </button>
            <button type="button" className="sst-menu-item sst-menu-item--danger" onClick={() => { setOpen(false); onDelete(); }}>
              Delete
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default function StudentSessionsTab({ student, permissions }) {
  const canManageReports = permissions ? !!permissions.manageReports : true;
  const studentId = String(student?.id || "");
  const studentName = String(student?.name || "").trim();

  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [error, setError] = useState("");

  const [reportsPage, setReportsPage] = useState(1);
  const [sessionsPage, setSessionsPage] = useState(1);

  // ── Load session logs + reports for this student ──
  useEffect(() => {
    if (!studentId || !studentName) {
      setLogs([]);
      setReports([]);
      return undefined;
    }
    let alive = true;
    setLogsLoading(true);
    setReportsLoading(true);
    setError("");

    Promise.all([
      fetchSessionLogsForStudent(studentName, { maxRows: 4000 }),
      fetchReportsForStudent(studentId),
    ])
      .then(([sessionLogs, rpts]) => {
        if (!alive) return;
        setLogs(sortByDateDesc(sessionLogs));
        setReports(rpts);
      })
      .catch((err) => {
        console.error("Failed to load student sessions/reports", err);
        if (alive) {
          setLogs([]);
          setReports([]);
          setError("Failed to load session data for this student.");
        }
      })
      .finally(() => {
        if (alive) {
          setLogsLoading(false);
          setReportsLoading(false);
        }
      });

    return () => {
      alive = false;
    };
  }, [studentId, studentName]);

  // Reset pagination when the student changes.
  useEffect(() => {
    setReportsPage(1);
    setSessionsPage(1);
  }, [studentId]);

  const metrics = useMemo(() => buildMetrics(logs), [logs]);

  const avgRating = useMemo(() => {
    const vals = logs.map((l) => calcAvgRating(l)).filter((v) => Number.isFinite(v));
    if (!vals.length) return null;
    return Math.round((vals.reduce((s, v) => s + v, 0) / vals.length) * 10) / 10;
  }, [logs]);

  const reportsPageCount = Math.max(1, Math.ceil(reports.length / REPORTS_PER_PAGE));
  const pagedReports = reports.slice((reportsPage - 1) * REPORTS_PER_PAGE, reportsPage * REPORTS_PER_PAGE);

  const sessionsPageCount = Math.max(1, Math.ceil(logs.length / SESSIONS_PER_PAGE));
  const pagedSessions = logs.slice((sessionsPage - 1) * SESSIONS_PER_PAGE, sessionsPage * SESSIONS_PER_PAGE);

  const openReport = (report) => {
    const id = report?.id || report?.reportId;
    if (id) window.open(`/progress-report/${id}`, "_blank");
  };

  const handleRename = async (report) => {
    const id = report?.id || report?.reportId;
    if (!id) return;
    const next = window.prompt("Rename report", report.customName || buildReportTitle(report));
    if (next == null) return;
    const trimmed = next.trim();
    if (!trimmed) return;
    try {
      await renameReport(id, trimmed);
      setReports((prev) => prev.map((r) => ((r.id || r.reportId) === id ? { ...r, customName: trimmed } : r)));
    } catch (err) {
      console.error("Rename failed", err);
      window.alert("Failed to rename report.");
    }
  };

  const handleDelete = async (report) => {
    const id = report?.id || report?.reportId;
    if (!id) return;
    if (!window.confirm(`Permanently delete "${buildReportTitle(report)}"?`)) return;
    try {
      await deleteReport(id);
      setReports((prev) => prev.filter((r) => (r.id || r.reportId) !== id));
    } catch (err) {
      console.error("Delete failed", err);
      window.alert("Failed to delete report.");
    }
  };

  const openSession = (log) => {
    if (!log?.id) return;
    localStorage.setItem("sessionLog_" + log.id, JSON.stringify(log));
    localStorage.setItem("tutorSession_" + log.id, JSON.stringify(log));
    window.open(`/session-detail?id=${log.id}`, "_blank");
  };

  return (
    <div className="student-sessions-tab sdw-stack">
      {error && <div className="sst-error">{error}</div>}

      {/* ── Summary metrics ── */}
      <section className="sdw-section">
        <div className="sdw-section__head">
          <div className="sdw-section__title">Summary</div>
          <div className="sdw-section__sub">{logsLoading ? "Loading…" : "All recorded sessions"}</div>
        </div>
        <div className="sst-stats">
          <div className="sst-stat">
            <div className="sst-stat__label">Sessions</div>
            <div className="sst-stat__value">{metrics.totalSessions}</div>
          </div>
          <div className="sst-stat">
            <div className="sst-stat__label">Total Hours</div>
            <div className="sst-stat__value">{metrics.totalHours.toFixed(1)}</div>
          </div>
          <div className="sst-stat">
            <div className="sst-stat__label">Homework Done</div>
            <div className="sst-stat__value">
              {metrics.homeworkCompletionRate === null ? "—" : `${metrics.homeworkCompletionRate}%`}
            </div>
          </div>
          <div className="sst-stat">
            <div className="sst-stat__label">Avg Rating</div>
            <div className="sst-stat__value sst-stat__value--stars">
              <StarRating value={avgRating} uid="sessions-summary" />
            </div>
          </div>
          <div className="sst-stat">
            <div className="sst-stat__label">Status</div>
            <div className={`sst-stat__value sst-stat__value--${riskTone(metrics.riskLevel)}`}>
              {metrics.riskLevel}
            </div>
          </div>
        </div>
      </section>

      {/* ── Generated reports ── */}
      <section className="sdw-section">
        <div className="sdw-section__head">
          <div className="sdw-section__title">Generated Reports</div>
          <div className="sdw-section__sub">
            {reportsLoading ? "Loading…" : `${reports.length} report${reports.length !== 1 ? "s" : ""}`}
          </div>
        </div>
        <div className="sdw-section__body">
          {reportsLoading ? (
            <div className="sdw-empty">Loading reports…</div>
          ) : reports.length === 0 ? (
            <div className="sdw-empty">No progress reports have been generated for this student yet.</div>
          ) : (
            <>
              <div className="sst-table-wrap">
                <table className="sst-table">
                  <thead>
                    <tr>
                      <th>Report</th>
                      <th>Generated</th>
                      <th>Date Range</th>
                      <th className="sst-num">Sessions</th>
                      <th aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {pagedReports.map((r) => (
                      <tr key={r.id || r.reportId} className="sst-row" onClick={() => openReport(r)}>
                        <td className="sst-strong">{buildReportTitle(r)}</td>
                        <td className="sst-muted">{r.generatedAt ? formatDateDisplay(toDateMaybe(r.generatedAt)) : "—"}</td>
                        <td className="sst-muted">{formatDateRange(r.startDate, r.endDate)}</td>
                        <td className="sst-num">{r.sessionCount ?? "—"}</td>
                        <td className="sst-actions-cell">
                          <ReportActions
                            canManage={canManageReports}
                            onOpen={() => openReport(r)}
                            onRename={() => handleRename(r)}
                            onDelete={() => handleDelete(r)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pager page={reportsPage} pageCount={reportsPageCount} onPage={setReportsPage} />
            </>
          )}
        </div>
      </section>

      {/* ── Session history ── */}
      <section className="sdw-section">
        <div className="sdw-section__head">
          <div className="sdw-section__title">Session History</div>
          <div className="sdw-section__sub">
            {logsLoading ? "Loading…" : `${logs.length} session${logs.length !== 1 ? "s" : ""}`}
          </div>
        </div>
        <div className="sdw-section__body">
          {logsLoading ? (
            <div className="sdw-empty">Loading session history…</div>
          ) : logs.length === 0 ? (
            <div className="sdw-empty">No sessions recorded for this student yet.</div>
          ) : (
            <>
              <div className="sst-table-wrap">
                <table className="sst-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Time</th>
                      <th>Tutor</th>
                      <th>Subject</th>
                      <th>Homework</th>
                      <th>Rating</th>
                      <th>Flag</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedSessions.map((log) => {
                      const hwStatus = String(log?.homeworkStatus || "—");
                      return (
                        <tr key={log.id} className="sst-row" onClick={() => openSession(log)}>
                          <td className="sst-strong sst-nowrap">{formatDateDisplay(log?.sessionStart || log?.sessionDateKey)}</td>
                          <td className="sst-muted sst-nowrap">
                            {log?.sessionStart ? `${formatTime(log.sessionStart)} – ${formatTime(log.sessionEnd)}` : "—"}
                          </td>
                          <td>{String(log?.tutorName || "—")}</td>
                          <td>{String(log?.subject || "—")}</td>
                          <td>
                            <span className={`sst-badge sst-badge--${hwBadgeTone(hwStatus)}`}>{hwStatus}</span>
                          </td>
                          <td><StarRating value={calcAvgRating(log)} uid={log?.id} /></td>
                          <td className="sst-muted">{String(log?.studentFlag || "—")}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <Pager page={sessionsPage} pageCount={sessionsPageCount} onPage={setSessionsPage} />
            </>
          )}
        </div>
      </section>
    </div>
  );
}
